import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './index.css';
import SEO from '../../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../../seo/pageSeo';

// SVG Icons
const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const ConfigIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 15.5A3.5 3.5 0 0 1 8.5 12 3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97 0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1 0 .33.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const models = [
  {
    label: 'ZM-160',
    width: '1600mm',
    specs: [
      { label: '最大面料重量', value: '200kg' },
      { label: '最大驱动速度', value: '110m/min' },
      { label: '功率', value: '5.5KW' },
      { label: '电源', value: '单相/三相' },
    ],
  },
  {
    label: 'ZM-190',
    width: '1900mm',
    specs: [
      { label: '最大面料重量', value: '200kg' },
      { label: '最大驱动速度', value: '110m/min' },
      { label: '功率', value: '5.5KW' },
      { label: '电源', value: '单相/三相' },
    ],
  },
  {
    label: 'ZM-210',
    width: '2100mm',
    specs: [
      { label: '最大面料重量', value: '200kg' },
      { label: '最大驱动速度', value: '110m/min' },
      { label: '功率', value: '5.5KW' },
      { label: '电源', value: '单相/三相' },
    ],
  },
  {
    label: 'ZM-220',
    width: '2200mm',
    specs: [
      { label: '最大面料重量', value: '200kg' },
      { label: '最大驱动速度', value: '110m/min' },
      { label: '功率', value: '5.5KW' },
      { label: '电源', value: '单相/三相' },
    ],
  },
];

const modules = [
  {
    num: '01',
    name: '复合材料专用',
    nameEn: 'Composite Material Design',
    nameVi: 'Thiết kế vật liệu composite',
    description:
      '子母机专为复合材料、多层布料与特种材料设计，固定主机配合移动子机同步作业，精密张力控制满足高性能材料铺设需求。',
    descriptionEn:
      'Mother-Child machine designed for composite materials, multi-layer fabrics and specialty materials. Fixed main unit with synchronized mobile sub-unit provides precise tension control.',
    descriptionVi:
      'Máy Mẹ-Con được thiết kế cho vật liệu composite, vải nhiều lớp và vật liệu đặc biệt. Đơn vị chính cố định với đơn vị phụ di động đồng bộ cung cấp kiểm soát lực căng chính xác.',
    imageUrl: 'https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/products/zm_hero.webp',
    align: 'left',
  },
  {
    num: '02',
    name: '智能控制系统',
    nameEn: 'Intelligent Control System',
    nameVi: 'Hệ thống điều khiển thông minh',
    description:
      '松下PLC配合欧姆龙触摸屏，西克传感器实时监控，控制系统稳定可靠。子机可独立移动，灵活应对不同布料幅宽。',
    descriptionEn:
      'Panasonic PLC with Omron touchscreen and SICK sensors for real-time monitoring. Stable and reliable control system with mobile sub-unit for flexible width adjustment.',
    descriptionVi:
      'Panasonic PLC với màn hình cảm ứng Omron và cảm biến SICK giám sát theo thời gian thực. Hệ thống điều khiển ổn định và đáng tin cậy với đơn vị phụ di động để điều chỉnh khổ linh hoạt.',
    imageUrl: 'https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/products/x6_module6.webp',
    align: 'right',
  },
];

function ZmModelCard({ model, index, t }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="zm-model-card"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="zm-model-card__label">{model.label}</div>
      <div className="zm-model-card__width">{model.width}</div>
      <div className="zm-model-card__divider" />
      <ul className="zm-model-card__specs">
        {model.specs.map((spec, i) => (
          <li key={i}>
            <strong>{spec.value}</strong>
            {t(spec.labelKey)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ZmModuleItem({ module, index, getModuleText }) {
  const itemRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const moduleName = getModuleText(module, 'name');
  const moduleDesc = getModuleText(module, 'description');

  return (
    <div
      ref={itemRef}
      className={`zm-module-item zm-module-item--${module.align}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="zm-module-item__media">
        <img
          src={module.imageUrl}
          alt={moduleName}
          className="zm-module-item__img"
          loading="lazy"
        />
        <div className="zm-module-item__badge">
          <span>{module.num}</span>
        </div>
      </div>

      <div className="zm-module-item__body">
        <div className="zm-module-item__num">
          {module.num} / {modules.length.toString().padStart(2, '0')}
        </div>
        <h3 className="zm-module-item__name">
          {moduleName}
          <br />
          <span style={{ fontSize: '0.55em', color: 'var(--color-primary)', fontWeight: 700 }}>
            {getModuleText(module, 'nameEn')}
          </span>
        </h3>
        <div className="zm-module-item__divider" />
        <p className="zm-module-item__desc">{moduleDesc}</p>
      </div>
    </div>
  );
}

export default function ZMMachine() {
  const { t, i18n } = useTranslation();

  const getModuleText = (module, field) => {
    const lang = i18n.language;
    if (lang.startsWith('vi')) return module[`${field}Vi`] || module[`${field}`] || module[`${field}En`];
    if (lang.startsWith('en')) return module[`${field}En`] || module[`${field}`];
    return module[field];
  };

  const getSpecLabelKey = (index) => {
    const keys = ['product_max_fabric_weight', 'product_max_drive_speed', 'product_power', 'product_power_source'];
    return keys[index] || 'product_max_fabric_weight';
  };

  const modelsWithSpecKeys = models.map(m => ({
    ...m,
    specs: m.specs.map((s, i) => ({ ...s, labelKey: getSpecLabelKey(i) }))
  }));

  const getConfigText = (zh, en, vi) => {
    const lang = i18n.language;
    if (lang.startsWith('vi')) return vi;
    if (lang.startsWith('en')) return en;
    return zh;
  };

  return (
    <>
    <SEO
        pageKey="zm"
        title={PAGE_SEO.zm.title}
        description={PAGE_SEO.zm.description}
        keywords={COMMON_KEYWORDS}
        slug={PAGE_SEO.zm.slug}
    />
    <div className="zm-page">
      {/* Hero */}
      <div className="zm-hero">
        <img
          className="zm-hero__img"
          src="https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/products/zm_hero.webp"
          alt="ZM Series"
        />
        <div className="zm-hero__overlay" />
        <div className="zm-hero__content">
          <div className="zm-hero__eyebrow">
            {t('zm_hero_eyebrow')}
          </div>
          <h1 className="zm-hero__title">
            {t('zm_hero_title')}
          </h1>
          <p className="zm-hero__sub">
            {t('zm_hero_sub')}
          </p>
          <div className="zm-hero__rule" />
        </div>
      </div>

      {/* Specs Section */}
      <section className="zm-specs-section">
        <div className="zm-section-header">
          <div className="zm-section-eyebrow">
            {t('zm_specs_eyebrow')}
          </div>
          <h2 className="zm-section-title">
            {t('zm_specs_title')}
          </h2>
          <div className="zm-section-rule" />
        </div>

        <div className="zm-model-cards">
          {modelsWithSpecKeys.map((model, index) => (
            <ZmModelCard key={model.label} model={model} index={index} t={t} />
          ))}
        </div>

        {/* Key Features */}
        <div className="zm-features-row">
          <div className="zm-feature-item">
            <div className="zm-feature-icon"><BoltIcon /></div>
            <div className="zm-feature-num">110<span className="zm-feature-unit">m/min</span></div>
            <div className="zm-feature-label">{t('zm_features_max_speed')}</div>
          </div>
          <div className="zm-feature-item">
            <div className="zm-feature-icon"><ConfigIcon /></div>
            <div className="zm-feature-num">200<span className="zm-feature-unit">kg</span></div>
            <div className="zm-feature-label">{t('zm_features_max_weight')}</div>
          </div>
          <div className="zm-feature-item">
            <div className="zm-feature-icon"><CheckIcon /></div>
            <div className="zm-feature-num">5.5<span className="zm-feature-unit">KW</span></div>
            <div className="zm-feature-label">{t('zm_features_rated_power')}</div>
          </div>
        </div>
      </section>

      {/* Module Showcase */}
      <section className="zm-modules-section">
        <div className="zm-section-header">
          <div className="zm-section-eyebrow">
            {t('zm_modules_eyebrow')}
          </div>
          <h2 className="zm-section-title">
            {t('zm_modules_title')}
          </h2>
          <div className="zm-section-rule" />
        </div>

        {modules.map((module, index) => (
          <ZmModuleItem key={module.num} module={module} index={index} getModuleText={getModuleText} />
        ))}
      </section>

      {/* Config Section */}
      <section className="zm-config-section">
        <div className="zm-section-header">
          <div className="zm-section-eyebrow">
            {t('zm_config_eyebrow')}
          </div>
          <h2 className="zm-section-title">
            {t('zm_config_title')}
          </h2>
          <div className="zm-section-rule" />
        </div>

        <div className="zm-config-grid">
          <div className="zm-config-card">
            <div className="zm-config-card__title">
              {t('zm_config_machine_title')}
            </div>
            <ul className="zm-config-list">
              {[
                getConfigText('固定主机一台', '1× Fixed Main Unit', '1× Đơn vị chính cố định'),
                getConfigText('放料机台一套', '1× Material Dispensing Unit', '1× Đơn vị cấp vải'),
                getConfigText('移动子机一台', '1× Mobile Sub-Unit', '1× Đơn vị phụ di động'),
                getConfigText('松下PLC控制系统', 'Panasonic PLC Control System', 'Hệ thống điều khiển Panasonic PLC'),
                getConfigText('欧姆龙触摸屏', 'Omron Touchscreen', 'Màn hình cảm ứng Omron'),
                getConfigText('西克传感器', 'SICK Sensors', 'Cảm biến SICK'),
                getConfigText('欧姆龙继电器', 'Omron Relays', 'Rơ le Omron'),
              ].map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="zm-config-card">
            <div className="zm-config-card__title">
              {t('zm_config_features_title')}
            </div>
            <ul className="zm-config-list">
              {[
                getConfigText('适用于复合材料、多层布料、特种材料', 'Suitable for composite, multi-layer & specialty fabrics', 'Phù hợp với vải composite, nhiều lớp & vật liệu đặc biệt'),
                getConfigText('固定主机 + 移动子机同步作业', 'Fixed main unit with synchronized mobile sub-unit', 'Đơn vị chính cố định với đơn vị phụ di động đồng bộ'),
                getConfigText('子机可独立移动，灵活应对不同幅宽', 'Mobile sub-unit adjusts to different fabric widths', 'Đơn vị phụ di động điều chỉnh theo các khổ vải khác nhau'),
                getConfigText('松下PLC + 欧姆龙触摸屏，系统稳定可靠', 'Panasonic PLC + Omron touchscreen for reliability', 'Panasonic PLC + màn hình cảm ứng Omron cho độ tin cậy'),
                getConfigText('西克传感器精确检测，张力控制精准', 'SICK sensors for precise tension control', 'Cảm biến SICK cho kiểm soát lực căng chính xác'),
                getConfigText('200kg大载重，满足重型材料需求', '200kg max weight for heavy materials', '200kg tải trọng tối đa cho vật liệu nặng'),
                getConfigText('110m/min高速驱动，生产效率更高', '110m/min high-speed drive for higher efficiency', 'Truyền động tốc độ cao 110m/phút để hiệu quả cao hơn'),
              ].map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="zm-cta">
        <div className="zm-cta__inner">
          <p className="zm-cta__text">
            {t('zm_cta_text')}
            <br />
            <span className="zm-cta__accent">
              {t('zm_cta_accent')}
            </span>
          </p>
          <p className="zm-cta__sub">
            {t('zm_cta_sub')}
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
